import React, { useContext, useState, useEffect } from "react";
import { themeContext } from "../../context/theme";
import styles from "./OnlineLineClearMultiplierDisplay.module.css";

export const OnlineLineClearMultiplierDisplay = ({ player, opponent, onlineGame, opponentTheme, dynamicColor, boardSkin }) => {
  const { theme } = useContext(themeContext);
  const [previousValues, setPreviousValues] = useState({
    singleLines: 0,
    doubleLines: 0,
    tripleLines: 0,
    tetrisLines: 0
  });
  const [animatingItems, setAnimatingItems] = useState({});

  // Get stats from either local player (onlineGame) or opponent data
  const stats = player ?
    onlineGame?.getGameStats() || {} :
    opponent || {};

  const multipliers = [
    { label: "1x", value: stats.singleLines || 0, key: "singleLines" },
    { label: "2x", value: stats.doubleLines || 0, key: "doubleLines" },
    { label: "3x", value: stats.tripleLines || 0, key: "tripleLines" },
    { label: "4x", value: stats.tetrisLines || 0, key: "tetrisLines" },
  ];

  // Use opponent theme for opponents, local theme for player
  // Normalize theme format (remove "theme-" prefix if present for CSS class lookup)
  let currentTheme = opponent ? (opponentTheme || theme) : theme;

  // Ensure theme is in correct format
  if (typeof currentTheme === 'string' && !currentTheme.startsWith('theme-')) {
    currentTheme = `theme-${currentTheme}`;
  } else if (typeof currentTheme === 'number') {
    currentTheme = `theme-${currentTheme}`;
  }

  // Determine the color to use:
  // 1. If a 3D board is selected (boardSkin exists), use the passed dynamicColor
  // 2. Otherwise, don't set inline style (let CSS handle it via theme classes for 2D mode)
  const dynamicValueStyle = (boardSkin && dynamicColor) ? { color: dynamicColor } : {};

  // Track value changes and trigger animations
  useEffect(() => {
    const newValues = {
      singleLines: stats.singleLines || 0,
      doubleLines: stats.doubleLines || 0,
      tripleLines: stats.tripleLines || 0,
      tetrisLines: stats.tetrisLines || 0
    };

    // Check for changes and trigger animations
    const newAnimatingItems = {};
    Object.keys(newValues).forEach(key => {
      if (newValues[key] > previousValues[key]) {
        newAnimatingItems[key] = true;
        // Remove animation class after animation completes
        setTimeout(() => {
          setAnimatingItems(prev => ({
            ...prev,
            [key]: false
          }));
        }, 300); // Match animation duration
      }
    });

    if (Object.keys(newAnimatingItems).length > 0) {
      setAnimatingItems(prev => ({
        ...prev,
        ...newAnimatingItems
      }));
    }

    setPreviousValues(newValues);
  }, [stats.singleLines, stats.doubleLines, stats.tripleLines, stats.tetrisLines]);

  return (
    <div className={styles.multiplierContainer}>
      <div className={`${styles.multiplierDisplay} ${
        // Only apply theme class if no board is selected (2D mode)
        !boardSkin ? (styles[`multiplierDisplay-${currentTheme}`] || '') : ''
      }`}>
        {multipliers.map((multiplier) => (
          <div
            key={multiplier.label}
            className={styles.multiplierItem}
          >
            <div
              className={`${styles.multiplierValue} ${
                animatingItems[multiplier.key] ? styles.multiplierValueUpdated : ''
              }`}
              style={dynamicValueStyle}
              key={multiplier.value} // Force re-render when value changes
            >
              {multiplier.value}
            </div>
            <div className={styles.multiplierLabel}>
              {multiplier.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};